// frontend/src/components/ProductionOverview.js (NY KOMPONENT)
import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { productionAPI, productsAPI } from '../services/api';
import { Edit, CheckCircle, Clock, Package, AlertTriangle, Save, X, Trash2, ArrowUpDown, Filter } from 'lucide-react';
import toast from 'react-hot-toast';

function ProductionOverview() {
  const [editingBatch, setEditingBatch] = useState(null);
  const [editForm, setEditForm] = useState({ quantity: '', notes: '' });
  const [sortField, setSortField] = useState('castDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedVariantId, setSelectedVariantId] = useState('');
  const queryClient = useQueryClient();

  const filterParams = useMemo(() => {
    const params = {};
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    if (selectedVariantId) params.variantId = selectedVariantId;
    return params;
  }, [dateFrom, dateTo, selectedVariantId]);

  const { data: batches, isLoading } = useQuery(
    ['production-batches', filterParams],
    () => productionAPI.getBatches(filterParams),
    {
      select: data => data.data,
      refetchInterval: 30000 // Refresh every 30 seconds
    }
  );

  const { data: products } = useQuery(
    'products-for-filter',
    () => productsAPI.getAll(),
    { select: data => data.data }
  );

  const variantOptions = useMemo(() => {
    if (!products) return [];
    return products.flatMap(product =>
      (product.variants || [])
        .filter(v => v.isActive !== false)
        .map(v => ({
          id: v.id,
          label: `${product.name} - ${v.sku}`
        }))
    );
  }, [products]);

  const hasActiveFilters = dateFrom || dateTo || selectedVariantId;

  const handleResetFilters = () => {
    setDateFrom('');
    setDateTo('');
    setSelectedVariantId('');
  };

  const updateBatchMutation = useMutation(
    ({ batchId, data }) => productionAPI.updateBatch(batchId, data),
    {
      onSuccess: (data) => {
        toast.success(data.data.message);
        setEditingBatch(null);
        queryClient.invalidateQueries('production-batches');
        queryClient.invalidateQueries(['stock', 'production-overview']);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Fejl ved opdatering');
      }
    }
  );

  const markReadyMutation = useMutation(
    (batchId) => productionAPI.markBatchReady(batchId),
    {
      onSuccess: (data) => {
        toast.success(data.data.message);
        queryClient.invalidateQueries('production-batches');
        queryClient.invalidateQueries(['stock', 'production-overview']);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Fejl ved markering som klar');
      }
    }
  );

  const deleteBatchMutation = useMutation(
    (batchId) => productionAPI.deleteBatch(batchId),
    {
      // Optimistic update - fjern batch fra UI med det samme
      onMutate: async (batchId) => {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries('production-batches');

        // Snapshot all active production-batches queries
        const queries = queryClient.getQueriesData('production-batches');

        // Optimistically remove the batch from all cached variants
        queries.forEach(([queryKey, data]) => {
          if (!data?.data) return;
          queryClient.setQueryData(queryKey, {
            ...data,
            data: data.data.filter(batch => batch.id !== batchId)
          });
        });

        // Return a context object with snapshots for rollback
        return { queries };
      },
      onSuccess: (data) => {
        toast.success(data.data.message);
        queryClient.invalidateQueries('production-batches');
        queryClient.invalidateQueries(['stock', 'production-overview']);
      },
      onError: (error, batchId, context) => {
        // If the mutation fails, restore all snapshots
        if (context?.queries) {
          context.queries.forEach(([queryKey, data]) => {
            queryClient.setQueryData(queryKey, data);
          });
        }
        toast.error(error.response?.data?.error || 'Fejl ved sletning af batch');
      }
    }
  );

  const handleEdit = (batch) => {
    setEditingBatch(batch.id);
    setEditForm({
      quantity: batch.quantity.toString(),
      notes: batch.notes || ''
    });
  };

  const handleSave = () => {
    if (!editForm.quantity || isNaN(editForm.quantity) || editForm.quantity < 1) {
      toast.error('Indtast et gyldigt antal');
      return;
    }

    updateBatchMutation.mutate({
      batchId: editingBatch,
      data: {
        quantity: parseInt(editForm.quantity),
        notes: editForm.notes
      }
    });
  };

  const handleMarkReady = (batchId) => {
    if (window.confirm('Er du sikker på at denne batch er klar til salg?')) {
      markReadyMutation.mutate(batchId);
    }
  };

  const handleDelete = (batchId, batchNumber) => {
    if (window.confirm(`Er du sikker på at du vil slette batch ${batchNumber}?\n\nDenne handling kan ikke fortrydes.`)) {
      deleteBatchMutation.mutate(batchId);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'CASTING': return <Package className="h-5 w-5 text-gray-500" />;
      case 'CURING': return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'READY': return <CheckCircle className="h-5 w-5 text-green-500" />;
      default: return <AlertTriangle className="h-5 w-5 text-red-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'CASTING': return 'bg-gray-100 text-gray-800';
      case 'CURING': return 'bg-yellow-100 text-yellow-800';
      case 'READY': return 'bg-green-100 text-green-800';
      case 'SHIPPED': return 'bg-blue-100 text-blue-800';
      default: return 'bg-red-100 text-red-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'CASTING': return 'Støbes';
      case 'CURING': return 'Hærder';
      case 'READY': return 'Klar til salg';
      case 'SHIPPED': return 'Afsendt';
      default: return status;
    }
  };

  const isOverdue = (batch) => {
    if (batch.status !== 'CURING') return false;
    return new Date() > new Date(batch.readyDate);
  };

  const sortedBatches = batches ? [...batches].sort((a, b) => {
    let aValue, bValue;

    switch (sortField) {
      case 'batchNumber':
        aValue = a.batchNumber || '';
        bValue = b.batchNumber || '';
        break;
      case 'productName':
        aValue = a.variant?.product?.name || '';
        bValue = b.variant?.product?.name || '';
        break;
      case 'sku':
        aValue = a.variant?.sku || '';
        bValue = b.variant?.sku || '';
        break;
      case 'status':
        // Sort by status priority: CASTING < CURING < READY < SHIPPED
        const statusOrder = { 'CASTING': 1, 'CURING': 2, 'READY': 3, 'SHIPPED': 4 };
        aValue = statusOrder[a.status] || 0;
        bValue = statusOrder[b.status] || 0;
        break;
      case 'quantity':
        aValue = a.quantity;
        bValue = b.quantity;
        break;
      case 'castDate':
        aValue = new Date(a.castDate).getTime();
        bValue = new Date(b.castDate).getTime();
        break;
      case 'readyDate':
        aValue = new Date(a.readyDate).getTime();
        bValue = new Date(b.readyDate).getTime();
        break;
      default:
        return 0;
    }

    if (typeof aValue === 'string') {
      const comparison = aValue.localeCompare(bValue, 'da-DK');
      return sortDirection === 'asc' ? comparison : -comparison;
    }

    if (aValue === bValue) return 0;
    const comparison = aValue < bValue ? -1 : 1;
    return sortDirection === 'asc' ? comparison : -comparison;
  }) : [];

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Produktions Oversigt</h2>
              <p className="text-sm text-gray-600 mt-1">
                Administrer produktionsbatches og marker salgsklare elementer
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <ArrowUpDown className="h-4 w-4 text-gray-500" />
              <select
                value={`${sortField}-${sortDirection}`}
                onChange={(e) => {
                  const [field, direction] = e.target.value.split('-');
                  setSortField(field);
                  setSortDirection(direction);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="castDate-desc">Støbedato (nyeste først)</option>
                <option value="castDate-asc">Støbedato (ældste først)</option>
                <option value="readyDate-asc">Klar dato (tidligst først)</option>
                <option value="readyDate-desc">Klar dato (senest først)</option>
                <option value="productName-asc">Produkt (A-Z)</option>
                <option value="productName-desc">Produkt (Z-A)</option>
                <option value="batchNumber-asc">Batch nummer (A-Z)</option>
                <option value="batchNumber-desc">Batch nummer (Z-A)</option>
                <option value="status-asc">Status (stigende)</option>
                <option value="status-desc">Status (faldende)</option>
                <option value="quantity-asc">Antal (laveste først)</option>
                <option value="quantity-desc">Antal (højeste først)</option>
              </select>
            </div>
          </div>

          {/* Filter sektion */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filtrer</span>
              {hasActiveFilters && (
                <button
                  onClick={handleResetFilters}
                  className="ml-auto text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  Nulstil filtre
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Støbedato fra</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Støbedato til</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  min={dateFrom || undefined}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Produktvariant</label>
                <select
                  value={selectedVariantId}
                  onChange={(e) => setSelectedVariantId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Alle varianter</option>
                  {variantOptions.map(v => (
                    <option key={v.id} value={v.id}>{v.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6">
          {!sortedBatches || sortedBatches.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              {hasActiveFilters ? (
                <>
                  <p>Ingen batches matcher de valgte filtre</p>
                  <button
                    onClick={handleResetFilters}
                    className="text-sm mt-2 text-blue-600 hover:text-blue-800 underline"
                  >
                    Nulstil filtre
                  </button>
                </>
              ) : (
                <>
                  <p>Ingen produktionsbatches endnu</p>
                  <p className="text-sm mt-1">Start med at registrere støbning i Produktion tab</p>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {sortedBatches.map((batch) => {
                const isEditing = editingBatch === batch.id;
                const overdue = isOverdue(batch);

                return (
                  <div 
                    key={batch.id} 
                    className={`border rounded-lg p-4 ${
                      overdue ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          {getStatusIcon(batch.status)}
                          <div>
                            <h3 className="font-medium text-gray-900">
                              {batch.variant?.product?.name} - {batch.variant?.sku}
                            </h3>
                            <p className="text-sm text-gray-600">
                              Batch: {batch.batchNumber}
                            </p>
                          </div>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(batch.status)}`}>
                            {getStatusText(batch.status)}
                          </span>
                          {overdue && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                              Forsinkelse
                            </span>
                          )}
                        </div>

                        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">Antal:</span>
                            {isEditing ? (
                              <input
                                type="number"
                                value={editForm.quantity}
                                onChange={(e) => setEditForm({...editForm, quantity: e.target.value})}
                                className="ml-2 w-20 px-2 py-1 border border-gray-300 rounded text-center"
                                min="1"
                              />
                            ) : (
                              <span className="ml-2 font-medium">{batch.quantity} stk</span>
                            )}
                          </div>
                          <div>
                            <span className="text-gray-500">Støbt:</span>
                            <span className="ml-2">{new Date(batch.castDate).toLocaleDateString('da-DK')}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Klar:</span>
                            <span className={`ml-2 ${overdue ? 'text-red-600 font-medium' : ''}`}>
                              {new Date(batch.readyDate).toLocaleDateString('da-DK')} {new Date(batch.readyDate).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        </div>

                        {(batch.notes || isEditing) && (
                          <div className="mt-3">
                            <span className="text-gray-500 text-sm">Bemærkninger:</span>
                            {isEditing ? (
                              <textarea
                                value={editForm.notes}
                                onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                                className="ml-2 w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                rows="2"
                              />
                            ) : (
                              <p className="ml-2 text-sm text-gray-700">{batch.notes}</p>
                            )}
                          </div>
                        )}

                        {overdue && (
                          <div className="mt-3 text-sm text-red-600">
                            <AlertTriangle className="inline h-4 w-4 mr-1" />
                            Denne batch skulle have været klar {new Date(batch.readyDate).toLocaleDateString('da-DK')}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center space-x-2">
                        {isEditing ? (
                          <>
                            <button
                              onClick={handleSave}
                              disabled={updateBatchMutation.isLoading}
                              className="p-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                              title="Gem ændringer"
                            >
                              <Save size={16} />
                            </button>
                            <button
                              onClick={() => setEditingBatch(null)}
                              className="p-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                              title="Annuller"
                            >
                              <X size={16} />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleEdit(batch)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                              title="Rediger batch"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(batch.id, batch.batchNumber)}
                              disabled={deleteBatchMutation.isLoading}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                              title="Slet batch"
                            >
                              <Trash2 size={16} />
                            </button>

                            {batch.status === 'CURING' && (
                              <button
                                onClick={() => handleMarkReady(batch.id)}
                                disabled={markReadyMutation.isLoading}
                                className="px-3 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50"
                                title="Marker som klar til salg"
                              >
                                Marker Klar
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProductionOverview;